// Google Analytics 4 (gtag.js) integration — funnel + conversion tracking.
//
// Goal (per product owner): NOT vanity "CTR", but acquisition-funnel
// insight — who arrives on the marketing site, who clicks through to
// download the worker app or register a practice, and where they drop
// off before becoming an account.
//
// This is now the ONLY analytics on the site: V1's first-party cookieless
// tracker (hooks/useTracking.js) posted to /api/track/*, which the V2 API
// does not serve, so it was dropped in the port. See lib/consent.js for a
// note on how the consent banner relates to the GA consent state below.
//
// Scope: this build IS the marketing site and nothing else, so every
// route here is tracked. (V1 shared one bundle across marketing +
// dashboard + admin and used siteMode.js to scope GA per host; the V2
// marketing app is a standalone static site, so that host gate is gone
// and the "is this route in scope?" answer is simply yes.)
//
// Cross-domain linking (LINKER_DOMAINS) is retained so a click-through
// from tempdash.app to the dashboard origin stitches into ONE GA4
// journey rather than two sessions.
//
// Privacy / law (UK GDPR + PECR): GA sets cookies, so it boots in
// Google Consent Mode v2 with EVERYTHING denied. Until the visitor
// accepts via the consent banner (components/CookieConsent.jsx) GA sends
// only cookieless pings. On accept we flip analytics_storage to granted.
// Ad signals stay denied permanently — we run analytics only, no ads.
//
// Activation: set VITE_GA_MEASUREMENT_ID (a G-XXXXXXXXXX web-stream id)
// in the build env. With it unset EVERY function here is a silent no-op,
// so dev/builds without the id behave exactly as before.

import { hasAnalyticsConsent, CONSENT_CHANGE_EVENT } from './consent'

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID

// Cross-domain linking: GA4 decorates outbound links between these hosts
// with the _gl param so the session survives the origin hop.
const LINKER_DOMAINS = ['tempdash.app', 'www.tempdash.app', 'dashboard.tempdash.app']

let booted = false

// ⛔ CONSENT IS READ FROM lib/consent.js, NEVER STORED HERE. This module used
// to keep its own copy under the same localStorage key, expecting a bare
// 'granted' string, while the banner writes a {v,analytics,ts} JSON record
// there. The two never agreed, so an Accept never reached GA: every visitor,
// accepted or not, was a cookieless ping (true of the live site too). The
// banner's record is the single source of truth; we only listen to it.

// Is GA configured at all? (Measurement id present + a real browser.)
export function gaConfigured() {
  return Boolean(GA_ID) && typeof window !== 'undefined'
}

// Should we track THIS path? This build only ever serves marketing
// routes, so the only gate left is "is GA configured at all?".
// (Signature kept — callers pass a path — so the host-scoping logic can
// be reintroduced here if this bundle ever serves more than marketing.)
export function shouldTrackPath() {
  return gaConfigured()
}

// Is the consent banner relevant here? (Anywhere GA can run.)
export function consentBannerApplies() {
  return gaConfigured()
}

// ── gtag bootstrap (lazy, idempotent) ──────────────────────────────
// Called on the first in-scope route. Sets Consent Mode v2 defaults
// BEFORE the gtag script loads (required ordering), restores a prior
// grant, then injects the tag.
function bootstrap() {
  if (booted || !gaConfigured()) return
  booted = true

  window.dataLayer = window.dataLayer || []
  function gtag() { window.dataLayer.push(arguments) }
  window.gtag = gtag

  // Consent Mode v2 — deny everything until the visitor opts in.
  // wait_for_update gives the banner a beat to apply a stored choice
  // before the first ping leaves, avoiding a flash of granted traffic.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  })

  // Returning visitor who already accepted — restore the grant now so
  // their first pageview this session is a full (cookied) hit.
  if (hasAnalyticsConsent()) {
    gtag('consent', 'update', { analytics_storage: 'granted' })
  }

  // Follow the banner live: Accept, Reject, or a later change from the
  // footer's Cookie settings, without a reload.
  window.addEventListener(CONSENT_CHANGE_EVENT, applyConsent)

  gtag('js', new Date())
  // send_page_view:false — this is an SPA, so we fire page_view manually
  // on every route change (see trackPageView) rather than once at load.
  gtag('config', GA_ID, {
    send_page_view: false,
    linker: { domains: LINKER_DOMAINS, accept_incoming: true },
  })

  const s = document.createElement('script')
  s.async = true
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  document.head.appendChild(s)
}

// Call on every route change. Boots gtag on the first in-scope route,
// then records the pageview. No-op off-scope / unconfigured.
export function trackPageView(path, title) {
  const p = path || (window.location.pathname + window.location.search)
  if (!shouldTrackPath(p)) return
  bootstrap()
  if (!window.gtag) return
  window.gtag('event', 'page_view', {
    page_path: p,
    page_location: window.location.href,
    page_title: title || document.title,
  })
}

// Fire a custom funnel/conversion event (app_store_click, sign_up, …).
// Safe to call anywhere — no-ops unless GA has booted on a tracked page.
export function trackEvent(name, params = {}) {
  if (!booted || !window.gtag) return
  window.gtag('event', name, params)
}

// ── Consent ────────────────────────────────────────────────────────
// Runs on every change to the banner's stored decision.
function applyConsent() {
  if (!window.gtag) return
  const granted = hasAnalyticsConsent()
  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
  })
  if (granted) {
    // Capture the current page as the first consented hit (the page_view
    // that fired pre-consent was a cookieless ping with no client id).
    trackPageView()
  } else {
    // Withdrawal: denying stops new writes, but cookies GA already set
    // would otherwise stay on the device after the lawful basis went.
    clearGaCookies()
  }
}

function clearGaCookies() {
  const names = document.cookie.split(';').map(c => c.split('=')[0].trim())
    .filter(n => n === '_ga' || n.startsWith('_ga_') || n === '_gid')
  const host = window.location.hostname
  const domains = ['', host, `.${host}`, '.tempdash.app']
  for (const n of names) {
    for (const d of domains) {
      document.cookie = `${n}=; Max-Age=0; path=/${d ? `; domain=${d}` : ''}`
    }
  }
}
